import * as React from 'react';
import { Button as TeamsnapUIButton, Modal } from '@teamsnap/teamsnap-ui';
import { Icon } from '@teamsnap/snap-ui';
import { currencyGivenPennies } from 'core/utils';
import { canBeAcceptedOrReleasedFromWaitlist } from 'components/registration/utils';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import api from 'core/api';
import './WaitlistModal.scss';
import { FormResult } from '../types';

type Props = {
  orgId: string;
  formId: string;
  selectedRegistrants?: FormResult[];
  show: boolean;
  type: 'accept' | 'release';
  onClose: () => any;
  onSubmit: () => any;
};

const content = {
  accept: {
    heading: 'Accept from Waitlist',
    description:
      'Registrant will receive an email notification of the acceptance and be prompted to complete payment.',
    subheading: 'Accepting',
    unableText: 'Unable to accept participant(s)',
    amountText: 'Amount Due',
  },
  release: {
    heading: 'Release from Waitlist',
    description: 'Registrants will not be notified.',
    subheading: 'Releasing',
    unableText: 'Unable to release previously released participant(s)',
    amountText: 'Amount Cancelled',
  },
};

const OfferEmail = `<p class="sui-mb-2">Registrant will receive the following email:</p>
      <p class="sui-mb-2">Congratulations!</p>
      <p class="sui-mb-2">[participant name] has been offered a spot in [registration option] at [org name].</p>
      <p class="sui-mb-2">Please click “Accept” and secure your spot.</p>`;

const isRegistrationWaitlisted = (registrant: FormResult) =>
  registrant?.waitlistStatus && registrant?.status === 'Complete';

const Button = ({
  type = 'success',
  children,
  className,
  onClick,
  disabled,
  ...props
}) => {
  let colorClasses = '';
  if (type === 'success') {
    colorClasses = 'sui-button--success';
  } else if (type === 'negative') {
    colorClasses = 'sui-button--negative';
  }

  return (
    <TeamsnapUIButton
      type="button"
      mods={`${colorClasses} ${className} `}
      onClick={onClick}
      isDisabled={disabled}
      {...props}
    >
      {children}
    </TeamsnapUIButton>
  );
};

const Tooltip = ({ children, tip, position }) => {
  const [showTip, setShowTip] = React.useState(false);

  const tooltipClassName = `tooltip tooltip-${position || 'top-center'}`;

  const handleClick = () => {
    setShowTip(!showTip);
  };

  return (
    <div
      data-testid="waitlist-tooltip"
      className={tooltipClassName}
      onClick={handleClick}
      onKeyDown={handleClick}
      role="button"
      tabIndex={0}
    >
      <span
        data-testid="waitlist-tooltip-toggle"
        className="sui-py-1 sui-label sui-text-blue-40"
      >
        {children}
      </span>
      {showTip && (
        <>
          <div data-testid="waitlist-tooltip-content" className="tooltip-box">
            <div dangerouslySetInnerHTML={{ __html: tip }} />
            <button
              className="sui-border-none sui-bg-transparent bg-text-white"
              type="button"
              onClick={() => setShowTip(!showTip)}
            >
              Got it
            </button>
          </div>
          <div className="tooltip-arrow" />
        </>
      )}
    </div>
  );
};

const WaitlistModal = (props: Props) => {
  const { orgId, formId, selectedRegistrants, show, type, onClose, onSubmit } =
    props;

  const [eligibleRegistrants, setEligibleRegistrants] = React.useState<
    FormResult[]
  >([]);
  const [notEligibleRegistrants, setNotEligibleRegistrants] = React.useState<
    FormResult[]
  >([]);
  const [showNotWaitlistedWarning, setShowNotWaitlistedWarning] =
    React.useState(false);

  React.useEffect(() => {
    const eligible = [];
    const nonEligible = [];

    setShowNotWaitlistedWarning(
      selectedRegistrants?.some((r) => !isRegistrationWaitlisted(r))
    );

    selectedRegistrants
      ?.filter((r) => isRegistrationWaitlisted(r))
      ?.map((registrant) => {
        if (canBeAcceptedOrReleasedFromWaitlist(registrant)) {
          eligible.push(registrant);
        } else {
          nonEligible.push(registrant);
        }
      });
    setEligibleRegistrants(eligible);
    setNotEligibleRegistrants(nonEligible);
  }, [selectedRegistrants]);

  const onCancel = () => {
    onClose();
  };

  const onSubmitAction = async () => {
    try {
      await api.post(`/organizations/${orgId}/update_waitlist/${formId}`, {
        form_result_ids: eligibleRegistrants
          .filter((r) => canBeAcceptedOrReleasedFromWaitlist(r))
          .map((record) => record.formResultId),
        type: type === 'accept' ? 'send_offer' : 'release',
      });
      reactiveFeedbackMessage({
        message:
          type === 'accept'
            ? 'Acceptance has been sent to participant(s).'
            : `${
                eligibleRegistrants.length === 1
                  ? eligibleRegistrants[0].participantFirstName
                  : 'Participant(s)'
              } have been released from waitlist.`,
        isError: false,
        autoclose: true,
      });
      onSubmit();
    } catch (e) {
      reactiveFeedbackMessage({
        message: `Oops! Failed to ${type} participant(s). Please try again or contact TeamSnap support for help.`,
        isError: true,
        autoclose: true,
      });
    }
  };

  return (
    <>
      {show && (
        <div id="waitlist-modal" data-testid="waitlist-modal">
          <Modal
            show={true}
            style={{ minWidth: '0', width: '565px', textAlign: 'start' }}
            closeFn={onCancel}
            heading={content[type].heading}
            showClose={false}
            allowOverlayClose={false}
          >
            <p className="sui-body-dense sui-mb-2 sui-text-gray-40">
              {content[type].description}
            </p>
            {eligibleRegistrants.length > 0 && (
              <>
                <p>{content[type].subheading}</p>
                <div className="players-list">
                  <div className="players-heading">
                    <strong>Player</strong>
                    <strong>Registration Option</strong>
                    <strong>{content[type].amountText}</strong>
                  </div>
                  <div className="eligible-players">
                    {eligibleRegistrants?.map((registrant, index) => (
                      <div
                        className="player"
                        data-testid={`eligible-player-${index}`}
                        key={`${registrant.participantFirstName}-${registrant.participantLastName}-${index}`}
                      >
                        <span data-testid={`player-name-${index}`}>
                          {`${registrant.participantFirstName} ${registrant.participantLastName}`}
                        </span>
                        <span data-testid={`player-group-${index}`}>
                          {registrant.participantParticipationGroup}
                        </span>
                        <span data-testid={`player-balance-${index}`}>
                          {Number.isInteger(registrant.orderBalance)
                            ? currencyGivenPennies(registrant.orderBalance)
                            : registrant.orderBalance}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                {type === 'accept' && (
                  <Tooltip tip={OfferEmail} position="top-left">
                    What will registrants see?
                  </Tooltip>
                )}
              </>
            )}
            {notEligibleRegistrants?.length > 0 && (
              <div
                data-testid="noteligible-warning"
                className="noteligible-warning sui-mt-2"
              >
                <span className="sui-text-red-5 sui-mr-1 sui-inline-flex sui-justify-center">
                  <Icon name="info" />
                </span>
                <div className="noteligible-list">
                  <>
                    {content[type].unableText}:{' '}
                    {notEligibleRegistrants.map((registrant, index) => (
                      <React.Fragment
                        key={`noteligible-${registrant?.participantFirstName}-${index}`}
                      >
                        {registrant.participantFirstName && (
                          <>
                            {registrant.participantFirstName}
                            {index !== notEligibleRegistrants.length - 1 &&
                              ', '}
                          </>
                        )}
                      </React.Fragment>
                    ))}
                  </>
                </div>
              </div>
            )}
            {showNotWaitlistedWarning && (
              <div
                data-testid="noteligible-warning"
                className="noteligible-warning"
              >
                <span className="sui-text-red-5 sui-mr-1 sui-inline-flex sui-justify-center">
                  <Icon name="info" />
                </span>
                <div className="noteligible-list">
                  Unable to {type} participant(s) not on the waitlist.
                </div>
              </div>
            )}

            <div className="sui-w-full sui-flex sui-justify-end sui-items-center sui-mt-4">
              <TeamsnapUIButton
                type="link"
                onClick={() => onCancel()}
                testId="waitlist-modal-cancel"
              >
                Cancel
              </TeamsnapUIButton>
              {type === 'accept' ? (
                <Button
                  type="success"
                  onClick={() => onSubmitAction()}
                  testId="waitlist-modal-submit"
                  className="sui-ml-2"
                  disabled={eligibleRegistrants.length === 0}
                >
                  Send
                </Button>
              ) : (
                <Button
                  type="negative"
                  onClick={() => onSubmitAction()}
                  testId="waitlist-modal-submit"
                  className="sui-ml-2"
                  disabled={eligibleRegistrants.length === 0}
                >
                  Release
                </Button>
              )}
            </div>
          </Modal>
        </div>
      )}
    </>
  );
};

export default WaitlistModal;
